import { useContext } from "react";
import { AuthContext, AuthContextType } from "@/context/AuthContext";
import { reviewApi } from "@/api/review";
import { useApiErrorHandler } from "@/lib/errorHandler";
import { toast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";

export interface TenantRestriction {
  hasAccess: boolean;
  limit: number;
  message: string;
  requestUpgrade: () => void;
}

export const useTenantRestriction = (
  resourceType: "User" | "Research",
): TenantRestriction => {
  const { values: contextValues, set } = useContext(
    AuthContext,
  ) as AuthContextType;
  const { planDetails, user } = contextValues;
  const { setLoading } = set;
  const { t } = useTranslation();

  const userLimit = parseInt(planDetails?.tenant_plan.user_limit || "1", 0);
  const errorHandler = useApiErrorHandler("useTenantRestriction", {});

  async function requestUpgrade() {
    try {
      setLoading?.(true);
      await reviewApi.requestUpgrade({
        token: user?.access,
      });
      toast({
        title: t("tenant.toast.upgrade_request_submitted"),
      });
    } catch (error) {
      errorHandler(error);
      toast({
        title: t("tenant.toast.upgrade_submit_failed"),
      });
    } finally {
      setLoading?.(false);
    }
  }

  if (resourceType === "User") {
    return {
      hasAccess: userLimit > 0,
      limit: userLimit,
      message: t("tenant.static_text.user_limit_message"),
      requestUpgrade: () => {
        requestUpgrade();
      },
    };
  } else if (resourceType === "Research") {
    return {
      hasAccess: planDetails?.tenant_plan.research_access || false,
      limit: 999999,
      message: t("tenant.static_text.scientific_research_limit_message"),
      requestUpgrade: () => {
        requestUpgrade();
      },
    };
  } else {
    return {
      hasAccess: true,
      limit: 999999,
      message: "",
      requestUpgrade: () => {
        requestUpgrade();
      },
    };
  }
};
